import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { Mail } from 'lucide-react';
import { cn } from '@/lib/utils';

interface NewsletterSignupProps {
  variant?: 'default' | 'footer' | 'sidebar';
  showPreferences?: boolean;
}

export function NewsletterSignup({ variant = 'default', showPreferences = false }: NewsletterSignupProps) {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [preferences, setPreferences] = useState({
    news: true,
    reviews: true,
    comparisons: true,
    videos: true,
    events: true,
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email) {
      toast.error('Please enter your email address');
      return;
    }

    setIsLoading(true);

    try {
      // Generate cryptographically secure tokens
      const generateSecureToken = (): string => {
        const randomBytes = new Uint8Array(32);
        crypto.getRandomValues(randomBytes);
        const base64 = btoa(String.fromCharCode(...randomBytes));
        return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
      };

      const { error } = await supabase
        .from('subscribers')
        .insert({
          email,
          pref_news: preferences.news,
          pref_reviews: preferences.reviews,
          pref_comparisons: preferences.comparisons,
          pref_videos: preferences.videos,
          pref_events: preferences.events,
          confirmation_token: generateSecureToken(),
          unsubscribe_token: generateSecureToken(),
        });

      if (error) {
        if (error.code === '23505') {
          toast.error('This email is already subscribed');
        } else {
          throw error;
        }
      } else {
        toast.success('Successfully subscribed!');
        setEmail('');
      }
    } catch (error) {
      console.error('Subscription error:', error);
      toast.error('Failed to subscribe. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const isFooter = variant === 'footer';
  const isSidebar = variant === 'sidebar';

  return (
    <form onSubmit={handleSubmit} className="w-full">
      <div className={cn(
        "flex gap-2",
        isSidebar ? "flex-col" : "flex-col sm:flex-row"
      )}>
        <div className="relative flex-1">
          <Mail className={cn(
            "absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4",
            isFooter ? "text-primary-foreground/50" : "text-muted-foreground"
          )} />
          <Input
            type="email"
            placeholder="Enter your email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className={cn(
              "pl-10",
              isFooter && "bg-primary-foreground/10 border-primary-foreground/20 text-primary-foreground placeholder:text-primary-foreground/50"
            )}
          />
        </div>
        <Button 
          type="submit" 
          disabled={isLoading}
          variant={isFooter ? "secondary" : "default"}
          className={isSidebar ? "w-full" : ""}
        >
          {isLoading ? 'Subscribing...' : 'Subscribe'}
        </Button>
      </div>

      {showPreferences && (
        <div className="mt-4 grid grid-cols-2 gap-2">
          {Object.entries(preferences).map(([key, value]) => (
            <div key={key} className="flex items-center space-x-2">
              <Checkbox
                id={`pref-${key}`}
                checked={value}
                onCheckedChange={(checked) =>
                  setPreferences(prev => ({ ...prev, [key]: checked === true }))
                }
              />
              <Label 
                htmlFor={`pref-${key}`} 
                className={cn(
                  "text-sm capitalize",
                  isFooter && "text-primary-foreground/80"
                )}
              >
                {key}
              </Label>
            </div>
          ))}
        </div>
      )}
    </form>
  );
}